import API_CONFIG from '../config/api';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

const LabelOptionsPanel = ({ map, layer, isOpen, onClose }) => {
  const { makeAuthenticatedRequest } = useAuth();
  const [labelSettings, setLabelSettings] = useState({
    visible: true,
    fontSize: 12,
    color: '#000000',
    haloColor: '#ffffff',
    haloWidth: 1,
    distance: 0.5,
    anchor: 'bottom', // 'bottom' anchor places text above the point
    column: ''
  });
  const [availableColumns, setAvailableColumns] = useState([]);

  const fetchAvailableColumns = async () => {
    try {
      // For CSV layers, first try to get the dataset info to check for CSV-specific data
      if (layer.geometry_type === 'CSV' || layer.geometry_type === 'Point') {
        const datasetResponse = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${layer.id}`);
        const datasetData = await datasetResponse.json();
        
        if (datasetData.success && datasetData.data) {
          const dataset = datasetData.data;
          
          // Check if this is a CSV layer with configuration data
          if (dataset.csv_data && dataset.csv_data.potentialColumnNames) {
            setAvailableColumns(dataset.csv_data.potentialColumnNames);
            return;
          }
        }
      }
      
      // Fallback to GeoJSON endpoint for non-CSV layers or if CSV approach fails
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${layer.id}/geojson`);
      const geojsonData = await response.json();
      
      // The /geojson endpoint returns raw GeoJSON, not wrapped in success/data
      if (geojsonData && geojsonData.features && geojsonData.features.length > 0) {
        // Get properties from the first feature to determine available columns
        const properties = geojsonData.features[0].properties || {};
        
        const columns = Object.keys(properties).filter(key => 
          // Filter out internal properties and include only meaningful columns
          !key.startsWith('_') || key === '_label'
        );
        setAvailableColumns(columns);
      } else {
        setAvailableColumns([]);
      }
    } catch (error) {
      console.error('Error fetching layer columns:', error);
      setAvailableColumns([]);
    }
  };

  useEffect(() => {
    if (!map || !layer || !isOpen) return;

    // Fetch available columns for the layer
    fetchAvailableColumns();

    const layerId = `layer-${layer.id}-labels`;
    
    // Check if label layer exists and get current settings
    if (map.getLayer(layerId)) {
      const currentVisibility = map.getLayoutProperty(layerId, 'visibility') !== 'none';
      const currentFontSize = map.getLayoutProperty(layerId, 'text-size') || 12;
      const currentColor = map.getPaintProperty(layerId, 'text-color') || '#000000';
      const currentHaloColor = map.getPaintProperty(layerId, 'text-halo-color') || '#ffffff';
      const currentHaloWidth = map.getPaintProperty(layerId, 'text-halo-width') || 1;
      const currentOffset = map.getLayoutProperty(layerId, 'text-offset') || [0, 0];
      const currentAnchor = map.getLayoutProperty(layerId, 'text-anchor') || 'bottom';
      
      // Calculate distance from offset (use the larger of x or y absolute values)
      const currentDistance = Math.max(Math.abs(currentOffset[0]), Math.abs(currentOffset[1]));

      setLabelSettings({
        visible: currentVisibility,
        fontSize: currentFontSize,
        color: currentColor,
        haloColor: currentHaloColor,
        haloWidth: currentHaloWidth,
        distance: currentDistance,
        anchor: currentAnchor,
        column: ''
      });
    }
  }, [map, layer, isOpen]);

  const calculateOffset = (anchor, distance) => {
    const offsetMap = {
      'bottom': [0, -distance],      // Above point
      'top': [0, distance],          // Below point
      'right': [-distance, 0],       // Left of point
      'left': [distance, 0],         // Right of point
      'center': [0, 0],              // On point
      'bottom-right': [-distance, -distance], // Top left
      'bottom-left': [distance, -distance],   // Top right
      'top-right': [-distance, distance],     // Bottom left
      'top-left': [distance, distance]        // Bottom right
    };
    
    return offsetMap[anchor] || [0, 0];
  };

  const updateLabelProperty = async (property, value) => {
    if (!map || !layer) return;

    const layerId = `layer-${layer.id}-labels`;
    
    if (!map.getLayer(layerId)) return;

    try {
      switch (property) {
        case 'visible':
          map.setLayoutProperty(layerId, 'visibility', value ? 'visible' : 'none');
          break;
        case 'fontSize':
          map.setLayoutProperty(layerId, 'text-size', value);
          break;
        case 'color':
          map.setPaintProperty(layerId, 'text-color', value);
          break;
        case 'haloColor':
          map.setPaintProperty(layerId, 'text-halo-color', value);
          break;
        case 'haloWidth':
          map.setPaintProperty(layerId, 'text-halo-width', value);
          break;
        case 'distance':
          const newOffset = calculateOffset(labelSettings.anchor, value);
          map.setLayoutProperty(layerId, 'text-offset', newOffset);
          break;
        case 'anchor':
          map.setLayoutProperty(layerId, 'text-anchor', value);
          // Also update offset based on current distance and new anchor
          const updatedOffset = calculateOffset(value, labelSettings.distance);
          map.setLayoutProperty(layerId, 'text-offset', updatedOffset);
          break;
        case 'column':
          await updateLabelColumn(value);
          break;
        default:
          break;
      }

      setLabelSettings(prev => ({
        ...prev,
        [property]: value
      }));
    } catch (error) {
      console.error('Error updating label property:', error);
    }
  };

  const updateLabelColumn = async (columnName) => {
    if (!map || !layer) return;

    try {
      // For CSV layers, we might need to reconfigure the CSV with the new label column
      if (layer.geometry_type === 'CSV' || layer.geometry_type === 'Point') {
        // First get the dataset info to check if it's a CSV layer
        const datasetResponse = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${layer.id}`);
        const datasetData = await datasetResponse.json();
        
        if (datasetData.success && datasetData.data && datasetData.data.csv_data) {
          // This is a CSV layer, we need to reconfigure it with the new label
          // Get the current configuration if it exists
          const currentConfig = datasetData.data.csv_data.configurationData || {};
          
          // Create new configuration with updated label column
          const newConfig = {
            columns: {
              x: currentConfig.columns?.x || (datasetData.data.csv_data.potentialColumnNames[1] || 'Column 2'),
              y: currentConfig.columns?.y || (datasetData.data.csv_data.potentialColumnNames[2] || 'Column 3'),
              label: columnName || ''
            },
            crs: currentConfig.crs || 'EPSG:4326',
            hasHeaders: currentConfig.hasHeaders !== undefined ? currentConfig.hasHeaders : true
          };
          
          // Reconfigure the CSV
          const configResponse = await makeAuthenticatedRequest(
            `/api/data/datasets/${layer.id}/configure-csv`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newConfig)
            }
          );
          
          if (configResponse.ok) {
            // Refresh the map data
            const updatedResponse = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${layer.id}/geojson`);
            const updatedGeojsonData = await updatedResponse.json();
            
            const sourceId = `source-${layer.id}`;
            if (map.getSource(sourceId) && updatedGeojsonData) {
              map.getSource(sourceId).setData(updatedGeojsonData);
            }
            return;
          }
        }
      }
      
      // Fallback: Standard approach for non-CSV layers
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${layer.id}/geojson`);
      const geojsonData = await response.json();
      
      // The /geojson endpoint returns raw GeoJSON
      if (geojsonData && geojsonData.features) {
        const sourceId = `source-${layer.id}`;
        
        // Update the _label property for each feature based on selected column
        geojsonData.features.forEach(feature => {
          if (columnName && feature.properties && feature.properties[columnName]) {
            feature.properties._label = feature.properties[columnName];
          } else {
            delete feature.properties._label;
          }
        });
        
        // Update the map source with the modified data
        if (map.getSource(sourceId)) {
          map.getSource(sourceId).setData(geojsonData);
        }
      }
    } catch (error) {
      console.error('Error updating label column:', error);
    }
  };

  const resetToDefaults = () => {
    const defaults = {
      visible: true,
      fontSize: 12,
      color: '#000000',
      haloColor: '#ffffff',
      haloWidth: 1,
      distance: 0.5,
      anchor: 'bottom', // 'bottom' anchor places text above the point
      column: ''
    };

    Object.keys(defaults).forEach(property => {
      updateLabelProperty(property, defaults[property]);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="layer-style-overlay">
      <div className="layer-style-panel">
        <div className="style-panel-header">
          <h3>Label Options: {layer?.name}</h3>
          <button className="btn-close-style" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="style-panel-content">
          <div className="layer-info-section">
            <p><strong>Geometry:</strong> {layer?.geometry_type}</p>
            <p><strong>Features:</strong> {layer?.feature_count}</p>
          </div>

          <div className="style-controls">
            {/* Label Visibility */}
            <div className="style-control">
              <label>
                <input
                  type="checkbox"
                  checked={labelSettings.visible}
                  onChange={(e) => updateLabelProperty('visible', e.target.checked)}
                />
                Show Labels
              </label>
            </div>

            {/* Label Column Selection */}
            <div className="style-control">
              <label>Label Column</label>
              <select
                value={labelSettings.column}
                onChange={(e) => updateLabelProperty('column', e.target.value)}
              >
                <option value="">No labels</option>
                {availableColumns.map(column => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </div>

            {labelSettings.visible && labelSettings.column && (
              <>
                {/* Font Size */}
                <div className="style-control">
                  <label>Font Size</label>
                  <input
                    type="range"
                    min="8"
                    max="24"
                    value={labelSettings.fontSize}
                    onChange={(e) => updateLabelProperty('fontSize', parseInt(e.target.value))}
                  />
                  <span>{labelSettings.fontSize}px</span>
                </div>

                {/* Text Color */}
                <div className="style-control">
                  <label>Text Color</label>
                  <input
                    type="color"
                    value={labelSettings.color}
                    onChange={(e) => updateLabelProperty('color', e.target.value)}
                  />
                </div>

                {/* Halo Color */}
                <div className="style-control">
                  <label>Halo Color</label>
                  <input
                    type="color"
                    value={labelSettings.haloColor}
                    onChange={(e) => updateLabelProperty('haloColor', e.target.value)}
                  />
                </div>

                {/* Halo Width */}
                <div className="style-control">
                  <label>Halo Width</label>
                  <input
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={labelSettings.haloWidth}
                    onChange={(e) => updateLabelProperty('haloWidth', parseFloat(e.target.value))}
                  />
                  <span>{labelSettings.haloWidth}px</span>
                </div>

                {/* Text Anchor */}
                <div className="style-control">
                  <label>Label Position</label>
                  <select
                    value={labelSettings.anchor}
                    onChange={(e) => updateLabelProperty('anchor', e.target.value)}
                  >
                    <option value="bottom">Above Point</option>
                    <option value="top">Below Point</option>
                    <option value="right">Left of Point</option>
                    <option value="left">Right of Point</option>
                    <option value="center">On Point</option>
                    <option value="bottom-right">Top Left</option>
                    <option value="bottom-left">Top Right</option>
                    <option value="top-right">Bottom Left</option>
                    <option value="top-left">Bottom Right</option>
                  </select>
                </div>

                {/* Distance */}
                <div className="style-control">
                  <label>Label Distance</label>
                  <input
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={labelSettings.distance}
                    onChange={(e) => updateLabelProperty('distance', parseFloat(e.target.value))}
                  />
                  <span>{labelSettings.distance}</span>
                </div>
              </>
            )}
          </div>

          <div className="style-panel-actions">
            <button className="btn-reset-style" onClick={resetToDefaults}>
              Reset to Default
            </button>
            <button className="btn-close-style-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabelOptionsPanel;